/**
 * AssistantMarkdown — the shared renderer for model-produced text.
 *
 * A thin wrapper over ReactMarkdown so every non-chat surface that shows
 * drafted/model output (Action History snippets & reasoning traces, citation
 * excerpts) renders GFM tables, headings, lists and ~sub~/^sup^ markers the
 * same way the chat bubble and Draft tab already do — instead of printing raw
 * markdown strings (the "table looks like a column of pipes" bug).
 *
 * Reuse, not re-creation:
 *   - assistantMarkdownRules (markdownStyles.ts) — the shared table/heading/list CSS
 *   - remarkSubSup + [remarkGfm, { singleTilde: false }] — the sub/sup contract
 *   - normalizeTableSpacing / decodeEscapedWhitespace (markdownNormalize.ts) —
 *     the pre-parse fixups that make single-"\n"-separated tables parse into <table>
 *   - repairPartialTables (partialTables.ts) — rebuilds tables cut off mid-excerpt
 *
 * Plain (non-markdown) text renders safely as paragraphs, so callers can pass
 * arbitrary retrieved snippets without pre-checking whether they contain markdown.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { remarkSubSup } from "../../services/remarkSubSup";
import { remarkSoftBreaks } from "../../services/remarkSoftBreaks";
import { decodeEscapedWhitespace, normalizeTableSpacing } from "../../services/markdownNormalize";
import { repairPartialTables } from "../../services/partialTables";
import { assistantMarkdownRules } from "./markdownStyles";

const useStyles = makeStyles({
  markdown: assistantMarkdownRules,
});

interface AssistantMarkdownProps {
  text: string;
  /** Optional extra class spread onto the wrapper (e.g. a card background). */
  className?: string;
}

const AssistantMarkdown: React.FC<AssistantMarkdownProps> = ({ text, className }) => {
  const styles = useStyles();
  const normalized = useMemo(() => normalizeTableSpacing(repairPartialTables(decodeEscapedWhitespace(text ?? ""))), [text]);
  return (
    <div className={className ? mergeClasses(styles.markdown, className) : styles.markdown}>
      {/* singleTilde:false is load-bearing — without it GFM strikethrough eats the
          `~sub~` markers at the micromark layer before remarkSubSup can see them. */}
      <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkSoftBreaks, remarkSubSup]}>
        {normalized}
      </ReactMarkdown>
    </div>
  );
};

export default AssistantMarkdown;
